import { surveyStatusEnum } from 'containers/SurveysPage/constants';
import CronConverter from 'cron-converter';
import moment from 'moment/moment';
import { pick, get, find } from 'lodash';
import Raven from 'raven-js';
import {
  SCHEDULING_POST_VALUES_MAP,
  MAX_REPEAT_DATES_COUNT,
  SCHEDULING_RECURRENCE,
  CRON_INTERVAL_MAP,
  CRON_INTERVAL_ONCE,
  CRON_INTERVAL_WEEKLY,
  CRON_INTERVAL_MONTHLY,
  CRON_INTERVAL_QUARTERLY,
  CRON_CUSTOM_DATES,
  DATE_FIELDS_ARRAY,
  CUSTOM_DATES_POST_KEYS,
  SCHEDULING_SCHEDULED,
  SCHEDULING_IMMEDIATE,
} from './constants';

export const getPostValues = (state) => {
  const { schedulingType: tempSchedulingType, interval, custom } = state;
  if (tempSchedulingType === SCHEDULING_IMMEDIATE) {
    return null;
  }

  let values;
  let currentCustomDate;
  if (interval === CRON_CUSTOM_DATES) {
    values = pick(state, SCHEDULING_POST_VALUES_MAP[CRON_CUSTOM_DATES]);
    values.custom = values.custom.map((customDate) =>
      pick(customDate, CUSTOM_DATES_POST_KEYS),
    );

    for (let i = 0; i < custom.length; i += 1) {
      currentCustomDate = values.custom[i];
      const isInstanceOfDate = currentCustomDate.date instanceof Date;
      const currentDate = isInstanceOfDate
        ? currentCustomDate.date
        : new Date(currentCustomDate.date);
      currentDate.setSeconds(0, 0);
      values.custom[i].date = currentDate.getTime();
    }
    values.startDate.setSeconds(0, 0);
    values.startDate = moment(values.startDate)
      .tz(state.timezone, true)
      .valueOf(); // startDate is kept in local timezone to avoid adjacent days
  } else {
    const schedulingType =
      interval === CRON_INTERVAL_ONCE
        ? SCHEDULING_SCHEDULED
        : tempSchedulingType;
    values = pick(state, SCHEDULING_POST_VALUES_MAP[schedulingType]);
    // convert dates to ms
    for (let i = 0; i < DATE_FIELDS_ARRAY.length; i += 1) {
      if (values[DATE_FIELDS_ARRAY[i]]) {
        values[DATE_FIELDS_ARRAY[i]].setSeconds(0, 0);
        values[DATE_FIELDS_ARRAY[i]] = moment(values[DATE_FIELDS_ARRAY[i]])
          .tz(state.timezone, true)
          .valueOf(); // start/end dates are kept in local timezone to avoid adjacent days
      }
    }
  }

  return values;
};

export const getTimeAsCronPart = (time) =>
  time && time.split(':').reverse().join(' ');

export const getCron = ({ time, startDate, interval, schedulingType }) => {
  if (
    schedulingType !== SCHEDULING_RECURRENCE ||
    interval === CRON_INTERVAL_ONCE ||
    !time ||
    !startDate
  ) {
    return null;
  }

  const timePart = getTimeAsCronPart(time);
  const monthDay = startDate.getDate();
  const weekDay = startDate.getDay();
  let cronPart;
  if (interval === CRON_INTERVAL_WEEKLY) {
    cronPart = CRON_INTERVAL_MAP[interval].replace('#', weekDay);
  } else if (
    interval === CRON_INTERVAL_MONTHLY ||
    interval === CRON_INTERVAL_QUARTERLY
  ) {
    cronPart = CRON_INTERVAL_MAP[interval].replace('#', monthDay);
  } else {
    cronPart = CRON_INTERVAL_MAP[interval];
  }

  return `${timePart} ${cronPart}`;
};

export const getNHoursInMs = (n) => n * 60 * 60 * 1000;

export const validStartDates = (today, endDate) => (current) => {
  if (current) {
    const dateAsMs = current.valueOf();
    const todayAndTwoHours = today + getNHoursInMs(2);
    const isInThePast =
      dateAsMs < todayAndTwoHours && !current.isSame(todayAndTwoHours, 'day');
    const isAfterEndDate = endDate ? dateAsMs > endDate.valueOf() : false;
    return isInThePast || isAfterEndDate;
  }
  return false;
};

export const validCustomStartDates = (today, existingDates) => (current) => {
  if (current) {
    const dateAsMs = current.valueOf();
    const todayAndTwoHours = today + getNHoursInMs(2);
    const isInThePast =
      dateAsMs < todayAndTwoHours && !current.isSame(todayAndTwoHours, 'day');
    const isExistingDate = find(
      existingDates,
      (currentExistingDate) =>
        currentExistingDate.date &&
        current.isSame(currentExistingDate.date, 'day'),
    );
    return isInThePast || isExistingDate;
  }
  return false;
};

export const validEndDates = (today, startDate) => (current) => {
  if (current) {
    if (startDate) {
      return (
        current.valueOf() < startDate.valueOf() &&
        !current.isSame(startDate, 'day')
      );
    }

    const dateAsMs = current.valueOf();
    return dateAsMs < today;
  }
  return false;
};

export const isPastDate = ({
  selectedDate,
  timezone,
  time,
  offset = getNHoursInMs(2),
}) => {
  if (!selectedDate || !timezone || !time) {
    return false;
  }

  const adjustedDate = getUpdatedMomentDate({
    selectedDate,
    timezone,
    time,
  });

  return adjustedDate.valueOf() < Date.now() + offset;
};

export const formatMomentDate = (
  date,
  timezone,
  noTimeOffset = true,
  format = 'MMM DD YYYY, HH:mm #Z',
) => {
  if (!date || !timezone) {
    return null;
  }
  date.tz(timezone, noTimeOffset);
  return date.format(format).replace('#', 'GMT');
};

export const getUpdatedMomentDate = ({ selectedDate, timezone, time }) => {
  if (!selectedDate || !time) {
    return null;
  }

  const [hours, minutes] = time.split(':');

  const resultDate = moment(selectedDate);
  if (timezone) {
    resultDate.tz(timezone, true);
  }
  resultDate.hours(hours);
  resultDate.minutes(minutes);
  resultDate.seconds(0);
  resultDate.milliseconds(0);

  return resultDate;
};

export const getUserTimezone = () => moment.tz.guess() || 'GMT';

export const getUpdatedDate = ({ selectedDate, timezone, time }) => {
  if (!selectedDate || !time) {
    return null;
  }
  let resultDate = getUpdatedMomentDate({ selectedDate, timezone, time });
  resultDate = resultDate.tz(getUserTimezone(), true);
  resultDate = resultDate.toDate();

  return resultDate;
};

export const getRepeatDates = ({
  startDate,
  endDate,
  time = '',
  timezone,
  cron,
  interval,
}) => {
  if (!cron || !endDate || !startDate || !timezone) {
    return null;
  }

  const firstRepeat = getUpdatedMomentDate({
    selectedDate: startDate.valueOf(),
    timezone,
    time,
  });
  const reference = moment(firstRepeat.valueOf());

  const momentDates = [];
  let currentDate = firstRepeat;
  let lastValidDate;

  let currentFormattedDate;

  const endDateAsMilliseconds = getUpdatedDate({
    selectedDate: endDate,
    timezone,
    time,
  }).getTime();

  let index = 0;
  const monthDay = firstRepeat.date();
  let otherDay = monthDay;
  let tempScheduler;
  let otherFirstRepeat;
  let otherReference;
  let otherCron;
  const otherCurrentFormattedDates = [];
  let quarterCounter = 0;

  let scheduler = new CronConverter();
  try {
    scheduler.fromString(cron);
    scheduler = scheduler.schedule(reference);
  } catch (ex) {
    return {
      dates: [],
      repeats: 0,
      extraDate: null,
    };
  }

  if (
    (interval === CRON_INTERVAL_MONTHLY ||
      interval === CRON_INTERVAL_QUARTERLY) &&
    monthDay > 28
  ) {
    const otherCurrentDates = [];
    const otherSchedulers = [];
    while (otherDay - 28 > 0) {
      otherFirstRepeat = getUpdatedMomentDate({
        selectedDate: moment(startDate).date(otherDay - 1),
        timezone,
        time,
      });
      otherCurrentDates.push(otherFirstRepeat);
      otherReference = moment(otherFirstRepeat.valueOf());
      tempScheduler = new CronConverter();
      otherCron = cron.replace(` ${monthDay}`, ` ${otherDay - 1}`); // space at the start is needed to prevent changing time
      tempScheduler.fromString(otherCron);
      tempScheduler = tempScheduler.schedule(otherReference);
      otherSchedulers.push(tempScheduler);
      otherDay -= 1;
    }

    while (
      currentDate.valueOf() < endDateAsMilliseconds ||
      currentDate.isSame(endDateAsMilliseconds, 'day')
    ) {
      currentFormattedDate = formatMomentDate(currentDate, timezone);
      for (let i = 0; i < otherCurrentDates.length; i += 1) {
        otherCurrentFormattedDates[i] = formatMomentDate(
          otherCurrentDates[i],
          timezone,
        );
      }

      lastValidDate = currentFormattedDate;
      if (index < MAX_REPEAT_DATES_COUNT) {
        if (momentDates[0] !== currentFormattedDate) {
          let hadLeapDate = false;
          // starts at the start for dates furthest to the end of month
          for (let i = 0; i < otherCurrentDates.length; i += 1) {
            if (!otherCurrentDates[i].isSame(currentDate, 'month')) {
              if (!hadLeapDate) {
                if (
                  (interval === CRON_INTERVAL_QUARTERLY &&
                    quarterCounter % 3 === 0) ||
                  interval !== CRON_INTERVAL_QUARTERLY
                ) {
                  momentDates.push(otherCurrentFormattedDates[i]);
                  hadLeapDate = true;
                  otherSchedulers[i].next();
                  quarterCounter += 1;
                  index += 1;
                }
              } else {
                otherSchedulers[i].next();
              }
            }
          }

          if (
            (interval === CRON_INTERVAL_QUARTERLY &&
              quarterCounter % 3 === 0 &&
              !hadLeapDate) ||
            interval !== CRON_INTERVAL_QUARTERLY
          ) {
            // MOD 3 because we want every three months
            momentDates.push(currentFormattedDate);
            index += 1;
          }
          quarterCounter += 1;
        } else {
          index -= 1;
        }
      }

      currentDate = scheduler.next();
      for (let i = 0; i < otherCurrentDates.length; i += 1) {
        otherCurrentDates[i] = otherSchedulers[i].next();
      }
    }

    return {
      dates:
        index > MAX_REPEAT_DATES_COUNT ? momentDates.splice(0, 5) : momentDates,
      repeats: index,
      extraDate: index > MAX_REPEAT_DATES_COUNT ? lastValidDate : null,
    };
  }

  // normal case

  while (
    currentDate.valueOf() < endDateAsMilliseconds ||
    currentDate.isSame(endDateAsMilliseconds, 'day')
  ) {
    currentFormattedDate = formatMomentDate(currentDate, timezone);
    lastValidDate = currentFormattedDate;
    if (index < MAX_REPEAT_DATES_COUNT) {
      if (momentDates[0] !== currentFormattedDate) {
        if (
          (interval === CRON_INTERVAL_QUARTERLY && quarterCounter % 3 === 0) ||
          interval !== CRON_INTERVAL_QUARTERLY
        ) {
          // MOD 3 because we want every three months
          momentDates.push(currentFormattedDate);

          if (interval === CRON_INTERVAL_QUARTERLY) {
            index += 1;
          }
        }
        quarterCounter += 1;
      } else {
        index -= 1;
      }
    }

    if (interval !== CRON_INTERVAL_QUARTERLY) {
      index += 1;
    }
    currentDate = scheduler.next();
  }

  return {
    dates:
      index > MAX_REPEAT_DATES_COUNT ? momentDates.splice(0, 5) : momentDates,
    repeats: index,
    extraDate: index > MAX_REPEAT_DATES_COUNT ? lastValidDate : null,
  };
};

const i18n = {
  'Etc/GMT+12': 'International Date Line West',
  'Pacific/Midway': 'Midway Island, Samoa',
  'Pacific/Honolulu': 'Hawaii',
  'America/Juneau': 'Alaska',
  'America/Dawson': 'Pacific Time (US and Canada); Tijuana',
  'America/Boise': 'Mountain Time (US and Canada)',
  'America/Chihuahua': 'Chihuahua, La Paz, Mazatlan',
  'America/Phoenix': 'Arizona',
  'America/Chicago': 'Central Time (US and Canada)',
  'America/Regina': 'Saskatchewan',
  'America/Mexico_City': 'Guadalajara, Mexico City, Monterrey',
  'America/Belize': 'Central America',
  'America/Detroit': 'Eastern Time (US and Canada)',
  'America/Indiana/Indianapolis': 'Indiana (East)',
  'America/Bogota': 'Bogota, Lima, Quito',
  'America/Glace_Bay': 'Atlantic Time (Canada)',
  'America/Caracas': 'Caracas, La Paz',
  'America/Santiago': 'Santiago',
  'America/St_Johns': 'Newfoundland and Labrador',
  'America/Sao_Paulo': 'Brasilia',
  'America/Argentina/Buenos_Aires': 'Buenos Aires, Georgetown',
  'America/Godthab': 'Greenland',
  'Etc/GMT+2': 'Mid-Atlantic',
  'Atlantic/Azores': 'Azores',
  'Atlantic/Cape_Verde': 'Cape Verde Islands',
  GMT: 'Dublin, Edinburgh, Lisbon, London',
  'Africa/Casablanca': 'Casablanca, Monrovia',
  'Atlantic/Canary': 'Canary Islands',
  'Europe/Belgrade': 'Belgrade, Bratislava, Budapest, Ljubljana, Prague',
  'Europe/Sarajevo': 'Sarajevo, Skopje, Warsaw, Zagreb',
  'Europe/Brussels': 'Brussels, Copenhagen, Madrid, Paris',
  'Europe/Amsterdam': 'Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
  'Africa/Algiers': 'West Central Africa',
  'Europe/Bucharest': 'Bucharest',
  'Africa/Cairo': 'Cairo',
  'Europe/Helsinki': 'Helsinki, Kiev, Riga, Sofia, Tallinn, Vilnius',
  'Europe/Athens': 'Athens, Istanbul, Minsk',
  'Asia/Jerusalem': 'Jerusalem',
  'Africa/Harare': 'Harare, Pretoria',
  'Europe/Moscow': 'Moscow, St. Petersburg, Volgograd',
  'Asia/Kuwait': 'Kuwait, Riyadh',
  'Africa/Nairobi': 'Nairobi',
  'Asia/Baghdad': 'Baghdad',
  'Asia/Tehran': 'Tehran',
  'Asia/Dubai': 'Abu Dhabi, Muscat',
  'Asia/Baku': 'Baku, Tbilisi, Yerevan',
  'Asia/Kabul': 'Kabul',
  'Asia/Yekaterinburg': 'Ekaterinburg',
  'Asia/Karachi': 'Islamabad, Karachi, Tashkent',
  'Asia/Kolkata': 'Chennai, Kolkata, Mumbai, New Delhi',
  'Asia/Kathmandu': 'Kathmandu',
  'Asia/Dhaka': 'Astana, Dhaka',
  'Asia/Colombo': 'Sri Jayawardenepura',
  'Asia/Almaty': 'Almaty, Novosibirsk',
  'Asia/Rangoon': 'Yangon Rangoon',
  'Asia/Bangkok': 'Bangkok, Hanoi, Jakarta',
  'Asia/Krasnoyarsk': 'Krasnoyarsk',
  'Asia/Shanghai': 'Beijing, Chongqing, Hong Kong SAR, Urumqi',
  'Asia/Kuala_Lumpur': 'Kuala Lumpur, Singapore',
  'Asia/Taipei': 'Taipei',
  'Australia/Perth': 'Perth',
  'Asia/Irkutsk': 'Irkutsk, Ulaanbaatar',
  'Asia/Seoul': 'Seoul',
  'Asia/Tokyo': 'Osaka, Sapporo, Tokyo',
  'Asia/Yakutsk': 'Yakutsk',
  'Australia/Darwin': 'Darwin',
  'Australia/Adelaide': 'Adelaide',
  'Australia/Sydney': 'Canberra, Melbourne, Sydney',
  'Australia/Brisbane': 'Brisbane',
  'Australia/Hobart': 'Hobart',
  'Asia/Vladivostok': 'Vladivostok',
  'Pacific/Guam': 'Guam, Port Moresby',
  'Asia/Magadan': 'Magadan, Solomon Islands, New Caledonia',
  'Pacific/Fiji': 'Fiji Islands, Kamchatka, Marshall Islands',
  'Pacific/Auckland': 'Auckland, Wellington',
  'Pacific/Tongatapu': "Nuku'alofa",
};

const timezones = Object.keys(i18n);

const geti18nEntry = (s) => {
  if (i18n && i18n[s]) {
    return i18n[s];
  }

  Raven.captureException('Missing timezone match', {
    extra: {
      timezone: s,
    },
  });
  return s;
};

// this is to get timezone options from moment. The good thing is it will show stuff even if name changes are introduced
// in a future version. A Raven call will be added to catch such a case.
export const getTimezoneOptions = () => {
  const options = moment.tz
    .names()
    .filter((tz) => timezones.includes(tz))
    .reduce((memo, tz) => {
      memo.push({
        name: tz,
        offset: moment.tz(tz).utcOffset(),
      });

      return memo;
    }, [])
    .sort((a, b) => a.offset - b.offset)
    .reduce((memo, tz) => {
      const timezone = tz.offset ? moment.tz(tz.name).format('Z') : '';

      memo.push({
        value: tz.name,
        label: `(GMT${timezone}) ${geti18nEntry(tz.name)}`,
      });
      return memo;
    }, []);
  return options;
};

export const getSchedulingAllowed = ({
  isScheduled,
  isRecurring,
  schedulingAllowed,
  statusName,
}) => {
  let isSchedulingAllowed = true;

  if (statusName === surveyStatusEnum.UNDER_EDIT) {
    if (schedulingAllowed) {
      isSchedulingAllowed = true;
    } else {
      isSchedulingAllowed = false;
    }
  } else if (
    statusName === surveyStatusEnum.COMPLETED &&
    !(isScheduled || isRecurring)
  ) {
    // means that it has at least 1 iteration
    isSchedulingAllowed = false;
  }

  return isSchedulingAllowed;
};
export const getLaunchNowAllowed = ({
  isRunning,
  isScheduled,
  isRecurring,
}) => {
  let isLaunchNowAllowed = true;
  if (isRunning || isScheduled || isRecurring) {
    isLaunchNowAllowed = false;
  }
  return isLaunchNowAllowed;
};

export const hasCustomDateError = (errors, refId) =>
  get(errors, `custom.${refId}.date`, false) === true;

export const hasCustomPastDateError = (errors, refId) =>
  get(errors, `custom.${refId}.pastDate`, false) === true;

export const hasCustomTimeError = (errors, refId) =>
  get(errors, `custom.${refId}.time`, false) === true;

export function getMillisecondFromDate(date) {
  if (moment.isMoment(date)) {
    return date.valueOf();
  }

  if (typeof date === 'string') {
    return new Date(date).getTime();
  }

  return date.getTime && date.getTime();
}

export function hasEqualTimes(time1, time2) {
  // assumed format is "12:30" or "1:0".
  try {
    const [time1Hours, time1Min] = time1.split(':');
    const [time2Hours, time2Min] = time2.split(':');

    return (
      parseInt(time1Hours, 10) === parseInt(time2Hours, 10) &&
      parseInt(time1Min, 10) === parseInt(time2Min, 10)
    );
  } catch (ex) {
    return false;
  }
}

export function hasEqualCustomDates(custom1 = [], custom2 = []) {
  try {
    if (custom1.length !== custom2.length) return false;

    return custom1.every((date1Config, index) => {
      const { date: date1, time: time1 } = date1Config;
      const date2Config = custom2[index];
      if (!date1Config) return false;
      const { date: date2, time: time2 } = date2Config;

      return (
        getMillisecondFromDate(date1) === getMillisecondFromDate(date2) &&
        hasEqualTimes(time1, time2)
      );
    });
  } catch (ex) {
    return false;
  }
}

export function hasOlderCustomDatesThanLaunchDate(
  launchDate,
  custom,
  interval,
) {
  if (interval !== CRON_CUSTOM_DATES) return false;

  try {
    return custom.some((dateConfig) => {
      const { date } = dateConfig;

      return getMillisecondFromDate(date) <= getMillisecondFromDate(launchDate);
    });
  } catch (ex) {
    return false;
  }
}
